/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { ReactNode } from 'react';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { useAppSelector } from '../../../app/hooks';
import { selectLatestPageById } from '../chat/chatSlice';
import { EditBadge } from '../chat/EditBadge';
import { HtmlFhirInstanceGrouped } from '../algorithm/types';
import styles from './FhirSidebarInner.module.scss';
import { FhirWidget } from './FhirWidget';
import { useMessageIndex, usePageIfExists } from '../chat/hooks/MessageContext';
import { fhirWidgetIdToRightPanelWidgetId } from './fhir_utils';
import clsx from 'clsx';

interface FhirSidebarInnerProps {
  compact?: boolean;
  className?: string;
  htmlFhirConfig: HtmlFhirInstanceGrouped[];
  documentVersionId: string;
  readOnly: boolean;
  messageIndex: number;
  onUpdate: () => void /* Causes the fhir widgets to move into the correct position */;
}
export function FhirSidebarInner({
  compact,
  htmlFhirConfig,
  readOnly,
  onUpdate,
  documentVersionId,
}: FhirSidebarInnerProps) {
  return (
    <>
      {htmlFhirConfig.map((widget, index) => (
        <FhirSidebarInnerWidgetWrapper
          key={index}
          widget={widget}
          readOnly={readOnly}
          onUpdate={onUpdate}
          documentVersionId={documentVersionId}
          compact={compact}
        />
      ))}
    </>
  );
}

interface FhirSidebarInnerWidgetWrapperProps {
  compact?: boolean;
  documentVersionId: string;
  readOnly: boolean;
  widget: HtmlFhirInstanceGrouped;
  onUpdate: () => void /* Causes the fhir widgets to move into the correct position */;
  editBadge?: ReactNode;
  onEditBadgeClick?: () => void;
  noPatient?: boolean /* If we are viewing this when editing a guide with ckeditor */;
  inEditorMode?: boolean /* If we are viewing this when editing a guide with ckeditor */;
}

interface FhirSidebarInnerWidgetProps extends FhirSidebarInnerWidgetWrapperProps {
  pageId?: string; // pageId is optional here because in the guide view, we get it from context, but in the non-guide view, we get it from props
  defaultUncollapsed?: boolean; // Normally we start hidden and then uncollapse in fhirUpdatePositions, but for testing and storybook it's easier to just start uncollapsed
}

/**
 * This is the main component that renders the FHIR widgets in the sidebar.
 *
 * Say we have two links to the same predefined widget, we want to hide the
 * second one. This is done in @see fhirUpdatePositions which adds the class 'collapsed-action'
 *
 * We should do better than this, but it's a start.
 *
 */
export function FhirSidebarInnerWidget({
  compact,
  readOnly,
  onUpdate,
  documentVersionId,
  widget,
  onEditBadgeClick,
  noPatient,
  inEditorMode,
  pageId,
  defaultUncollapsed,
}: FhirSidebarInnerWidgetProps) {
  const { embedded_doc_id, pageId: widgetPageId } = widget;
  if (widgetPageId === 'LANDING') {
    throw new Error('FhirSidebarInnerWidget: pageId is LANDING - i.e. the tabs page. Should be a guide inside it');
  }
  const messageIndex = useMessageIndex();

  /** A given widget can have multipe <fhir> that point to it.  For the purpose of the id, we only need the first one */
  // FIXME: Actually maybe this is why we have bugs with multiple widgets?
  const widgetId = widget.fhirWidgetIds[0]!;
  /** id must match format in @see fhirUpdatePositions and @see setFhirHighlight */
  const rightPanelWidgetId = fhirWidgetIdToRightPanelWidgetId(
    widgetId,
    widget.predefinedWidgetId,
    messageIndex,
    !!inEditorMode
  );
  // Note we show even if widget.fhirWidgetIds.length > 1, meaning that we are editing multiple widgets at once
  const showEditBadge = onEditBadgeClick && pageId && !embedded_doc_id;

  if (!inEditorMode && !widget.predefinedWidgetId) {
    return null; // Don't show anything in consumer mode if not configured
  }

  return (
    <div
      /** @see fhirUpdatePositions will dynamically add/remove the collapsed-action class */
      className={clsx(styles.container, defaultUncollapsed || inEditorMode ? null : 'collapsed-action', {
        [styles.inEditorMode!]: inEditorMode,
        [styles.compact!]: compact,
      })}
      id={rightPanelWidgetId}
      data-element-id={widgetId}
      data-fhir-predefinedwidget={widget.predefinedWidgetId}
      key={widgetId}
      data-private
    >
      <FhirWidget
        readOnly={readOnly}
        onUpdate={onUpdate}
        fhir={widget}
        noPatient={noPatient}
        pageId={pageId}
        documentVersionId={documentVersionId}
        inEditorMode={inEditorMode}
      >
        {showEditBadge && (
          // applying compact styles similar to FHIRWidgetPanelContent, to keep edit icon relative to panel
          // we need to keep this consistent with FHIRWidgetPanel.tsx compact styling
          <div
            className={`edit-fhir-badge ${styles.editBadgeContainer}
                        ${noPatient || compact ? styles.editBadgeCompact : ''}`}
          >
            {!widget.predefinedWidgetId && (
              <div title="Not a widget">
                <ReportProblemIcon fontSize="small" color="error" />
              </div>
            )}
            <EditBadge onClick={onEditBadgeClick} />
          </div>
        )}
      </FhirWidget>
    </div>
  );
}

/** A wrapper for FhirSidebarInnerWidget to read information from the store */
export function FhirSidebarInnerWidgetWrapper(props: FhirSidebarInnerWidgetWrapperProps) {
  const { embedded_doc_id, pageId } = props.widget;
  /* Okay, this is really really ugly.
     Let me try to explain what is happening, urgh!

     - Viewing from a guide: usePageIfExists is set
     - Viewing from a not-editing (not flipped) message: usePageIfExists is not set, and instead use selectPageById
     - Viewing from a not-editing (not flipped) message: usePageIfExists is not set, and instead use selectPageById
       but we also need to check for unsaved changes, so we use selectUnsavedPageChangesById, and then combine these two.

   */
  let pageFromContext = usePageIfExists()?.page; // i.e. viewing from a guide
  if (pageFromContext?.id !== pageId) {
    pageFromContext = undefined; // Want to return if found, but can't here because of the use hooks rules
  }

  const page = useAppSelector((state) =>
    embedded_doc_id
      ? state.chat.publishedDocuments[embedded_doc_id]?.pages[pageId]
      : pageFromContext ?? selectLatestPageById(state, props.documentVersionId, pageId)
  );
  return <FhirSidebarInnerWidget {...props} pageId={page?.id} />;
}
